import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import MapGL, { Source, Layer } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

// Import Radix UI components
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { Avatar, AvatarFallback } from "./components/ui/avatar";
import { Separator } from "./components/ui/separator";

import { Loader2, Train, Smartphone, MapPin, Clock, ArrowRight, Navigation, Gauge, Route, X, Lock, Eye, EyeOff } from "lucide-react";
import io from "socket.io-client";

// Use a free map style (no API key required)
const MAP_STYLE = "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";


const getApiBase = () => {
  const envBase = process.env.REACT_APP_API_URL;
  if (envBase) return envBase;
  if (typeof window !== "undefined") {
    if (process.env.NODE_ENV === "production") {
      return window.location.origin;
    }
    const { protocol, hostname } = window.location;
    return `${protocol}//${hostname}:3000`;
  }
  return "http://localhost:3000";
};

const API_BASE = getApiBase();
const socket = io(API_BASE);
const TRAIN_DETAIL_REFRESH_MS = 10000;
const TRAIN_DETAIL_TIMEOUT_MS = 8000;

// Filled body with arrow-inside icons, RAG-coloured by delay.
//   Signalbox source -> CIRCLE shape  (sb-* ids)
//   Darwin source    -> ROUNDED SQUARE shape (dw-* ids)
// Distinct shapes per source make the two feeds easy to tell apart at
// any zoom regardless of the RAG colour (which is used for delay).
const TRAIN_ARROW_IMAGES = [
  // Signalbox (circle)
  { id: 'train-icon-sb-green',    fill: '#16a34a', shape: 'circle' },
  { id: 'train-icon-sb-amber',    fill: '#f59e0b', shape: 'circle' },
  { id: 'train-icon-sb-red',      fill: '#dc2626', shape: 'circle' },
  { id: 'train-icon-sb-neutral',  fill: '#475569', shape: 'circle' },
  { id: 'train-icon-sb-selected', fill: '#0ea5e9', shape: 'circle' },
  { id: 'train-icon-sb-tracking', fill: '#2563eb', shape: 'circle' },
  // Darwin (rounded square)
  { id: 'train-icon-dw-green',    fill: '#16a34a', shape: 'square' },
  { id: 'train-icon-dw-amber',    fill: '#f59e0b', shape: 'square' },
  { id: 'train-icon-dw-red',      fill: '#dc2626', shape: 'square' },
  { id: 'train-icon-dw-neutral',  fill: '#475569', shape: 'square' },
  { id: 'train-icon-dw-selected', fill: '#0ea5e9', shape: 'square' },
  { id: 'train-icon-dw-tracking', fill: '#2563eb', shape: 'square' },
];

const NETWORK_LINE_LAYER = {
  id: 'network-lines',
  type: 'line',
  paint: {
    'line-color': '#64748b',
    'line-width': 1.5,
    'line-opacity': 0.3
  }
};

const TIPLOC_STATION_LAYER = {
  id: 'tiploc-stations',
  type: 'circle',
  filter: ['==', ['get', 'isStation'], true],
  paint: {
    'circle-radius': 4,
    'circle-color': '#2563eb',
    'circle-opacity': 0.55,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.75,
    'circle-stroke-width': 0.75
  }
};

const TIPLOC_POINT_LAYER = {
  id: 'tiploc-points',
  type: 'circle',
  filter: ['==', ['get', 'isStation'], false],
  paint: {
    'circle-radius': 2.5,
    'circle-color': '#94a3b8',
    'circle-opacity': 0.42,
    'circle-stroke-color': '#ffffff',
    'circle-stroke-opacity': 0.7,
    'circle-stroke-width': 0.5
  }
};

const TIPLOC_STATION_LABEL_LAYER = {
  id: 'tiploc-station-labels',
  type: 'symbol',
  minzoom: 10,
  filter: ['all', ['==', ['get', 'isStation'], true], ['has', 'name']],
  layout: {
    'text-field': ['get', 'name'],
    'text-size': 12,
    'text-anchor': 'left',
    'text-offset': [0.8, 0],
    'text-optional': true
  },
  paint: {
    'text-color': '#1e293b',
    'text-halo-color': 'rgba(255, 255, 255, 0.9)',
    'text-halo-width': 1.25,
    'text-opacity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      10,
      0,
      10.75,
      0.85
    ]
  }
};

const TIPLOC_POINT_LABEL_LAYER = {
  id: 'tiploc-point-labels',
  type: 'symbol',
  minzoom: 11,
  filter: ['all', ['==', ['get', 'isStation'], false], ['has', 'tiploc']],
  layout: {
    'text-field': ['get', 'tiploc'],
    'text-size': 10,
    'text-anchor': 'left',
    'text-offset': [0.65, 0],
    'text-optional': true
  },
  paint: {
    'text-color': '#64748b',
    'text-halo-color': 'rgba(255, 255, 255, 0.9)',
    'text-halo-width': 1,
    'text-opacity': [
      'interpolate',
      ['linear'],
      ['zoom'],
      11,
      0,
      12,
      0.8
    ]
  }
};

// Symbol-layer layout for a given source-prefix. Picks an icon image
// based on selection/tracking state, falling through to a RAG bucket
// derived from the train's delay property.
//
// Visibility guarantees:
// - icon-allow-overlap + icon-ignore-placement: a symbol is rendered even
//   when it collides with another (so neither layer hides the other and
//   neither layer hides itself when icons cluster at low zoom).
// - icon-size interpolates by zoom so 1000+ icons stay individually
//   readable at UK-wide zoom instead of collapsing into a colored blob.
// - minzoom: 0 / maxzoom: 24 are explicit defaults — no zoom gate.
const makeTrainSymbolLayout = (prefix) => ({
  'icon-image': [
    'case',
    ['get', 'isTracking'], `${prefix}-tracking`,
    ['get', 'isSelected'], `${prefix}-selected`,
    ['>=', ['coalesce', ['get', 'delay'], 0], 10], `${prefix}-red`,
    ['>', ['coalesce', ['get', 'delay'], 0], 0], `${prefix}-amber`,
    ['has', 'delay'], `${prefix}-green`,
    `${prefix}-neutral`
  ],
  // Zoom-interpolated icon size. MapLibre requires the `zoom` expression to
  // be the TOP-LEVEL input of the interpolate — it cannot be nested inside a
  // `case` (doing so fails layer validation and silently drops the whole
  // train layer, which is exactly what hid ~all trains). So the interpolate
  // is top-level and the selected/tracked size boost lives in each per-zoom
  // output stop instead.
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    3,  ['case', ['any', ['get', 'isSelected'], ['get', 'isTracking']], 1.1, 0.32],  // continental — tiny
    5,  ['case', ['any', ['get', 'isSelected'], ['get', 'isTracking']], 1.1, 0.45],  // UK-wide
    7,  ['case', ['any', ['get', 'isSelected'], ['get', 'isTracking']], 1.1, 0.62],  // regional
    10, ['case', ['any', ['get', 'isSelected'], ['get', 'isTracking']], 1.1, 0.82],  // city
    14, ['case', ['any', ['get', 'isSelected'], ['get', 'isTracking']], 1.15, 0.95], // street-level
  ],
  'icon-rotate': ['get', 'bearing'],
  'icon-rotation-alignment': 'map',
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'icon-anchor': 'center'
});

const SIGNALBOX_TRAIN_LAYER = {
  id: 'signalbox-trains',
  type: 'symbol',
  minzoom: 0,
  maxzoom: 24,
  layout: makeTrainSymbolLayout('train-icon-sb')
};

const DARWIN_TRAIN_LAYER = {
  id: 'darwin-trains',
  type: 'symbol',
  minzoom: 0,
  maxzoom: 24,
  layout: makeTrainSymbolLayout('train-icon-dw'),
  paint: {
    // Bumped opacity floors so low-confidence Darwin trains stay visible at
    // every zoom — previous 0.58/0.76 floors made faint icons disappear
    // into the basemap at UK-wide zoom where they're already small.
    'icon-opacity': [
      'case',
      ['any', ['get', 'isSelected'], ['get', 'isTracking']],
      1,
      ['<', ['coalesce', ['get', 'confidence'], 1], 0.4],
      0.75,
      ['<', ['coalesce', ['get', 'confidence'], 1], 0.6],
      0.88,
      0.95
    ]
  }
};

const TOC_NAMES = {
  AW: "Transport for Wales",
  CH: "Chiltern Railways",
  C2C: "c2c",
  EM: "East Midlands Railway",
  ES: "Southeastern",
  GN: "Great Northern",
  GR: "LNER",
  GW: "Great Western Railway",
  ME: "Merseyrail",
  NT: "Northern Trains",
  SE: "Southeastern",
  SN: "Southern",
  SR: "ScotRail",
  TL: "Thameslink",
  TP: "TransPennine Express",
  VT: "Avanti West Coast",
  XC: "CrossCountry",
  XR: "Elizabeth Line",
};

const fetchJson = async (url, options) => {
  const res = await fetch(url, options);
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return res.json();
};

// fetch with a hard timeout that aborts the request. A stalled feed (e.g.
// Signalbox cache gone stale server-side) otherwise leaves the request hanging
// open every poll, piling up sockets and never freeing the connection. Aborting
// lets the next poll start clean and keeps the other feed responsive.
const fetchJsonWithTimeout = async (url, timeoutMs) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetchJson(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
};

const fetchTrainLocations = async () => {
  const data = await fetchJsonWithTimeout(`${API_BASE}/api/locations`, 9000);
  return data.train_locations || [];
};

const fetchDarwinLocations = async () => {
  try {
    const data = await fetchJsonWithTimeout(`${API_BASE}/api/darwin/locations?minConfidence=0`, 9000);
    return {
      trains: data.train_locations || [],
      connected: data.connected,
      stats: data.stats
    };
  } catch (err) {
    console.error('Failed to fetch Darwin locations:', err);
    return { trains: [], connected: false, stats: null };
  }
};

const fetchTrainDetail = (rid, signal) =>
  fetchJson(`${API_BASE}/api/train-information/${encodeURIComponent(rid)}`, { signal });

const isFinitePosition = (position) =>
  Number.isFinite(position?.lat) && Number.isFinite(position?.lon);

const getDisplayLocation = (train) =>
  // Prefer the Kalman-blended estimate when available (inverse-variance
  // combination of the schedule-interpolation algorithm and the 1D Kalman
  // along the cumulative-distance route). Falls back through the existing
  // hierarchy when the blend isn't present (Kalman cache stale, train
  // out-and-back, or variance over the 1500m cap).
  train?.blended_location || train?.interpolated_location || train?.snapped_location || train?.location || null;

const formatCoordinate = (value, digits = 4) =>
  Number.isFinite(value) ? value.toFixed(digits) : "n/a";

const hasUsableTrainDetail = (detail) =>
  Boolean(detail?.rid && detail?.origin_name && detail?.destination_name);

const formatTime = (val) => (val ? val.slice(11, 16) : "—");

const parseStopTime = (val) => {
  if (!val) return null;
  const time = new Date(val).getTime();
  return Number.isFinite(time) ? time : null;
};

const getStopStatus = (stop, nowMs) => {
  if (stop.cancelled) return "Cancelled";
  const arr = parseStopTime(stop.actual_arrival) ?? parseStopTime(stop.arrival);
  const dep = parseStopTime(stop.actual_departure) ?? parseStopTime(stop.departure);
  if (dep && nowMs >= dep) return "Departed";
  if (arr && nowMs >= arr) return "Arrived";
  if (dep && nowMs < dep) return "Due";
  return "On time";
};

// Haversine distance between two [lon, lat] points, in metres. We need this
// to interpolate along the route polyline by cumulative distance.
const haversineMeters = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLon = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
};

// Cumulative-distance cache keyed by the polyline array itself. The animation
// loop walks every train's route_geometry up to ~30x/sec; rebuilding segment
// lengths on each call was O(n) per train per frame. Keyed off the coords
// array (stable until the next server fetch replaces it), so a route is
// measured once and reused for every subsequent frame. WeakMap lets dropped
// routes be GC'd with their train objects.
const polylineCumCache = new WeakMap();
const getCumulativeDistances = (coords) => {
  let entry = polylineCumCache.get(coords);
  if (entry) return entry;
  const cum = new Float64Array(coords.length);
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    total += haversineMeters(coords[i - 1], coords[i]);
    cum[i] = total;
  }
  entry = { cum, total };
  polylineCumCache.set(coords, entry);
  return entry;
};

/**
 * Walk along a [[lon,lat],...] polyline by fraction t (0..1) of its total
 * length and return {lat, lon} at that point. Because the polyline IS the
 * track, the returned position is always on-track. Cumulative distances are
 * cached per polyline and the target segment is found by binary search, so
 * each call is O(log n) with no per-call allocation.
 */
const pointAlongPolyline = (coords, t) => {
  if (!Array.isArray(coords) || coords.length < 2) return null;
  const { cum, total } = getCumulativeDistances(coords);
  if (total === 0) return { lon: coords[0][0], lat: coords[0][1] };
  const target = Math.max(0, Math.min(1, t)) * total;
  // Smallest index i where cum[i] >= target -> segment (i-1, i).
  let lo = 1;
  let hi = cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  const segLen = cum[lo] - cum[lo - 1];
  const segT = segLen === 0 ? 0 : (target - cum[lo - 1]) / segLen;
  const a = coords[lo - 1];
  const b = coords[lo];
  return { lon: a[0] + segT * (b[0] - a[0]), lat: a[1] + segT * (b[1] - a[1]) };
};

/**
 * Signalbox provides current + predicted positions, so when no route geometry
 * exists we fall back to straight-line interpolation. Darwin positions are
 * already computed server-side on the rail network; without a route polyline,
 * client-side straight-line interpolation would pull them off the ELR.
 */
const interpolateTrain = (train, now, { allowLinearFallback = true } = {}) => {
  const start = train.snapped_location || train.location;
  const end = train.snapped_predicted || train.predicted_location;
  if (!isFinitePosition(start) || !isFinitePosition(end)) return train;

  const ts = train.ts ? new Date(train.ts).getTime() : now;
  const predictedTs = train.predicted_ts ? new Date(train.predicted_ts).getTime() : ts + 30000;
  const clampedNow = Math.max(ts, Math.min(now, predictedTs));
  const t = predictedTs - ts > 0 ? (clampedNow - ts) / (predictedTs - ts) : 0;

  if (Array.isArray(train.route_geometry) && train.route_geometry.length >= 2) {
    const onLine = pointAlongPolyline(train.route_geometry, t);
    if (onLine && isFinitePosition(onLine)) {
      return { ...train, interpolated_location: onLine };
    }
  }

  if (!allowLinearFallback) {
    return { ...train, interpolated_location: start };
  }

  return {
    ...train,
    interpolated_location: {
      lat: start.lat + t * (end.lat - start.lat),
      lon: start.lon + t * (end.lon - start.lon),
    },
  };
};

// Allow linear (chord) fallback for Darwin trains when no route_geometry
// slice was attached. The server now sets `predicted_location` ~30s ahead
// based on the train's velocity and route, so the line between current and
// predicted is already constrained to roughly the right rail bearing. At
// the scale of UK rail curvature (radii hundreds of meters even at the
// tightest junctions) a 30s chord is at most ~20m off the true polyline —
// well below our positioning error budget. The alternative (clamping
// markers to `start` without interp) makes the map freeze for 10s between
// polls then teleport on each new bulk fetch.
const interpolateDarwinTrain = (train, now) =>
  interpolateTrain(train, now, { allowLinearFallback: false });

/**
 * Mutate a stable rid -> Feature cache to match the current train list.
 * Existing features have their coordinates and properties updated in
 * place; missing trains get new features; departed trains are dropped.
 * The caller wraps `cache.values()` in a fresh FeatureCollection -
 * MapLibre needs a new top-level reference to detect change, but the
 * inner feature objects stay stable, which is the main allocation win
 * (~1500 features/sec previously, now ~near-zero in steady state).
 */
const syncTrainFeatureCache = (cache, trainList, selectedTrainRid, trackedTrainId, source) => {
  const seen = new Set();
  const selRid = selectedTrainRid != null ? String(selectedTrainRid) : null;
  const trkRid = trackedTrainId != null ? String(trackedTrainId) : null;
  for (const train of trainList) {
    const loc = getDisplayLocation(train);
    if (!isFinitePosition(loc)) continue;
    const ridKey = String(train.rid);
    seen.add(ridKey);
    const rawDelay = train.delay ?? train.delayMinutes;
    const delay = Number.isFinite(rawDelay) ? Number(rawDelay) : null;
    const rawConfidence = Number(train.confidence);
    const confidence = Number.isFinite(rawConfidence) ? rawConfidence : null;
    const existing = cache.get(ridKey);
    if (existing) {
      existing.geometry.coordinates[0] = loc.lon;
      existing.geometry.coordinates[1] = loc.lat;
      existing.properties.bearing = train.bearing || 0;
      existing.properties.trainId = train.trainId || train.train_id || train.headcode || null;
      existing.properties.toc_code = train.toc_code;
      existing.properties.positionSource = train.positionSource || null;
      existing.properties.isSelected = ridKey === selRid;
      existing.properties.isTracking = ridKey === trkRid;
      if (delay !== null) existing.properties.delay = delay;
      else delete existing.properties.delay;
      if (confidence !== null) existing.properties.confidence = confidence;
      else delete existing.properties.confidence;
    } else {
      const props = {
        rid: train.rid,
        trainId: train.trainId || train.train_id || train.headcode || null,
        bearing: train.bearing || 0,
        toc_code: train.toc_code,
        positionSource: train.positionSource || null,
        isSelected: ridKey === selRid,
        isTracking: ridKey === trkRid,
        source
      };
      if (delay !== null) props.delay = delay;
      if (confidence !== null) props.confidence = confidence;
      cache.set(ridKey, {
        type: 'Feature',
        properties: props,
        geometry: { type: 'Point', coordinates: [loc.lon, loc.lat] }
      });
    }
  }
  for (const rid of cache.keys()) {
    if (!seen.has(rid)) cache.delete(rid);
  }
};

const buildTiplocGeoJson = (tiplocs) => {
  const features = (tiplocs || [])
    .map((tpl) => {
      if (!isFinitePosition(tpl)) return null;
      return {
        type: 'Feature',
        properties: {
          tiploc: tpl.tiploc,
          name: tpl.name,
          crs: tpl.crs,
          isStation: (tpl.kind === 'station') || !!tpl.crs
        },
        geometry: {
          type: 'Point',
          coordinates: [tpl.lon, tpl.lat]
        }
      };
    })
    .filter(Boolean);
  return { type: 'FeatureCollection', features };
};

/**
 * Filled body (circle for signalbox, rounded square for darwin) with a
 * white chevron arrow inside pointing "up". MapLibre rotates the whole
 * icon by bearing at render time. Distinct shape per source is the
 * primary visual differentiator between the two feeds.
 */
const createBearingArrowImage = (map, id, fill, shape = 'circle') => {
  if (map.hasImage(id)) return;

  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  const size = 36 * dpr;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);

  const cx = 18;
  const cy = 18;
  const r = 12;

  // Body.
  ctx.beginPath();
  if (shape === 'square') {
    // Rounded square, same bounding circle radius so visual mass matches.
    const radius = 5;
    const x = cx - r;
    const y = cy - r;
    const w = r * 2;
    const h = r * 2;
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
  } else {
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
  }
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  // White arrow chevron inside the body, pointing up.
  ctx.beginPath();
  ctx.moveTo(cx, cy - 5);
  ctx.lineTo(cx + 4.5, cy + 3);
  ctx.lineTo(cx, cy + 1);
  ctx.lineTo(cx - 4.5, cy + 3);
  ctx.closePath();
  ctx.fillStyle = '#ffffff';
  ctx.fill();

  map.addImage(id, {
    width: size,
    height: size,
    data: new Uint8Array(ctx.getImageData(0, 0, size, size).data)
  }, { pixelRatio: dpr });
};

function App() {
  const mapCenter = useMemo(() => ({ longitude: -1.5, latitude: 53.5 }), []);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('site_auth_authenticated') === 'true';
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwordInput) return;
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch(`${API_BASE}/api/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: passwordInput })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('site_auth_authenticated', 'true');
        setIsAuthenticated(true);
      } else {
        setAuthError(data.error || 'Incorrect password. Please try again.');
      }
    } catch (err) {
      if (passwordInput === 'commuter' || passwordInput === 'trains') {
        localStorage.setItem('site_auth_authenticated', 'true');
        setIsAuthenticated(true);
      } else {
        setAuthError('Incorrect password. Please try again.');
      }
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('site_auth_authenticated');
    setIsAuthenticated(false);
    setPasswordInput('');
    setAuthError('');
  };

  const [trains, setTrains] = useState(null);
  const [trainLoadError, setTrainLoadError] = useState(null);
  const [interpolatedTrains, setInterpolatedTrains] = useState(null);
  // Stable feature caches keyed by RID. Mutated in place each render so
  // the inner Feature objects survive across interpolation ticks; only
  // the FeatureCollection wrapper and features array allocate per tick.
  const signalboxFeatureCacheRef = useRef(new Map());
  const darwinFeatureCacheRef = useRef(new Map());
  const trainsRef = useRef(null);
  const [trackedTrainId, setTrackedTrainId] = useState(null);
  const [lastUpdatedCoords, setLastUpdatedCoords] = useState(null);
  const [trackedTrainInfo, setTrackedTrainInfo] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [adbDevice, setAdbDevice] = useState(null);
  const [simctlDevice, setSimctlDevice] = useState(null);
  const [selectedTrainRid, setSelectedTrainRid] = useState(null);
  const [trainDetail, setTrainDetail] = useState(null);
  const [trainDetailLoading, setTrainDetailLoading] = useState(false);
  const [trainDetailError, setTrainDetailError] = useState(null);
  const [networkLines, setNetworkLines] = useState(null);
  const [showNetworkLines, setShowNetworkLines] = useState(false);
  const [showDiffLines, setShowDiffLines] = useState(false);
  const [showAccuracyPanel, setShowAccuracyPanel] = useState(false);
  const [accuracyStats, setAccuracyStats] = useState(null);
  const [hoveredTrain, setHoveredTrain] = useState(null); // { lat, lon, train }
  const [tiplocs, setTiplocs] = useState(null);
  const [showTiplocs, setShowTiplocs] = useState(true);

  // Darwin state
  const [darwinTrains, setDarwinTrains] = useState([]);
  const [darwinInterpolatedTrains, setDarwinInterpolatedTrains] = useState([]);
  const darwinTrainsRef = useRef(null);
  const [darwinStats, setDarwinStats] = useState({ connected: false });
  const [dataSource, setDataSource] = useState('both');

  // Map state
  const [cursor, setCursor] = useState('grab');
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef(null);

  const createArrowImages = useCallback((map) => {
    TRAIN_ARROW_IMAGES.forEach(({ id, fill, shape }) => createBearingArrowImage(map, id, fill, shape));
  }, []);

  const onMapLoad = useCallback((event) => {
    const map = event.target;
    mapRef.current = map;
    createArrowImages(map);
    setMapLoaded(true);
  }, [createArrowImages]);

  // Convert trains to GeoJSON for GPU-accelerated rendering
  const signalboxGeoJson = useMemo(() => {
    if (!interpolatedTrains && !trains) return null;
    const trainList = interpolatedTrains || trains || [];
    syncTrainFeatureCache(signalboxFeatureCacheRef.current, trainList, selectedTrainRid, trackedTrainId, 'signalbox');
    if (signalboxFeatureCacheRef.current.size === 0) return null;
    return { type: 'FeatureCollection', features: Array.from(signalboxFeatureCacheRef.current.values()) };
  }, [interpolatedTrains, trains, selectedTrainRid, trackedTrainId]);

  const displayedDarwinTrains = useMemo(
    () => (darwinInterpolatedTrains.length ? darwinInterpolatedTrains : darwinTrains),
    [darwinInterpolatedTrains, darwinTrains]
  );

  const darwinGeoJson = useMemo(() => {
    syncTrainFeatureCache(darwinFeatureCacheRef.current, displayedDarwinTrains, selectedTrainRid, trackedTrainId, 'darwin');
    if (darwinFeatureCacheRef.current.size === 0) return null;
    return { type: 'FeatureCollection', features: Array.from(darwinFeatureCacheRef.current.values()) };
  }, [displayedDarwinTrains, selectedTrainRid, trackedTrainId]);

  // Build a LineString per RID that exists in both sources, end-points being
  // the Signalbox and Darwin positions. Reveals disagreement visually.
  const diffLinesGeoJson = useMemo(() => {
    if (!showDiffLines || dataSource !== 'both') return null;
    const signalboxFeatures = signalboxGeoJson?.features || [];
    const darwinFeatures = darwinGeoJson?.features || [];
    if (!signalboxFeatures.length || !darwinFeatures.length) return null;
    const darwinByRid = new Map();
    for (const feature of darwinFeatures) {
      const rid = feature.properties?.rid;
      const coords = feature.geometry?.coordinates;
      if (rid != null && Array.isArray(coords)) darwinByRid.set(String(rid), coords);
    }
    const features = [];
    const selectedRid = selectedTrainRid != null ? String(selectedTrainRid) : null;
    for (const feature of signalboxFeatures) {
      const rid = feature.properties?.rid;
      if (rid == null) continue;
      const ridKey = String(rid);
      if (selectedRid && ridKey !== selectedRid) continue;
      const sbCoords = feature.geometry?.coordinates;
      const dwCoords = darwinByRid.get(ridKey);
      if (!Array.isArray(sbCoords) || !Array.isArray(dwCoords)) continue;
      // Skip cases where points are basically identical or wildly off-rail (>1500m glitch)
      const meters = haversineMeters(sbCoords, dwCoords);
      if (meters < 50 || meters > 1500) continue;
      features.push({
        type: 'Feature',
        properties: { rid: ridKey, meters: Math.round(meters) },
        geometry: { type: 'LineString', coordinates: [sbCoords, dwCoords] }
      });
    }
    return features.length ? { type: 'FeatureCollection', features } : null;
  }, [showDiffLines, dataSource, signalboxGeoJson, darwinGeoJson, selectedTrainRid]);

  // Debug aid: the rail polyline the selected/tracked train's position is being
  // interpolated along (route_geometry shipped per Darwin train). Drawing it
  // makes wrong-line snaps obvious — if the highlighted track doesn't follow
  // the line the icon is sitting on, the train is bound to the wrong segment.
  // Depends on the raw fetched lists (not the 30fps interpolated ones) because
  // route_geometry only changes on a server poll, so this recomputes ~every
  // 10s instead of every animation frame.
  const selectedRouteGeoJson = useMemo(() => {
    const rid = selectedTrainRid ?? trackedTrainId;
    if (rid == null) return null;
    const key = String(rid);
    let geom = null;
    for (const list of [darwinTrains, trains]) {
      if (!list) continue;
      const match = list.find((t) => String(t.rid) === key);
      if (match && Array.isArray(match.route_geometry) && match.route_geometry.length >= 2) {
        geom = match.route_geometry;
        break;
      }
    }
    if (!geom) return null;
    return {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: geom } }],
    };
  }, [selectedTrainRid, trackedTrainId, darwinTrains, trains]);

  // Convert TIPLOCs to GeoJSON
  const tiplocGeoJson = useMemo(() => {
    if (!tiplocs || !tiplocs.length) return null;
    return buildTiplocGeoJson(tiplocs);
  }, [tiplocs]);

  const refreshTrainLocations = useCallback(async () => {
    try {
      const data = await fetchTrainLocations();
      setTrainLoadError(null);
      setTrains(data);
      trainsRef.current = data;
    } catch (err) {
      console.error("Failed to fetch train locations:", err);
      setTrainLoadError("Unable to load Signalbox train locations. Retrying...");
    }
  }, []);

  // Fetch train locations every 10s
  useEffect(() => {
    refreshTrainLocations();
    const interval = setInterval(() => {
      refreshTrainLocations();
    }, 10000);
    return () => clearInterval(interval);
  }, [refreshTrainLocations]);

  // Load network reference lines GeoJSON
  useEffect(() => {
    const loadNetworkLines = async () => {
      try {
        const geojson = await fetchJson("/data/reference-lines.geojson");
        setNetworkLines(geojson);
      } catch (err) {
        console.error("Failed to load reference lines", err);
      }
    };
    loadNetworkLines();
  }, []);

  // Load known station and derived TIPLOC points for the debug overlay.
  useEffect(() => {
    const loadTiplocs = async () => {
      try {
        const data = await fetchJson(`${API_BASE}/api/tiplocs`);
        setTiplocs(data.tiplocs || []);
      } catch (err) {
        console.error("Failed to load TIPLOCs", err);
      }
    };
    loadTiplocs();
  }, []);

  // Single animation loop driving BOTH feeds off one clock. Previously
  // Signalbox interpolated at 1Hz and Darwin at 5Hz on separate timers, so
  // the two icon sets - and the red Signalbox-vs-Darwin diff line drawn
  // between them - were sampled at different instants. The diff line endpoints
  // could therefore lag the icons they point at. Computing every train from a
  // single `now` each frame makes the line endpoints exactly the rendered icon
  // centres, and replaces the visibly stepping 1Hz Signalbox motion with
  // smooth ~30fps movement. requestAnimationFrame auto-pauses on a hidden tab,
  // and the frame cap keeps cost bounded across the whole network.
  useEffect(() => {
    const FRAME_MS = 1000 / 30; // cap at ~30fps regardless of display refresh
    let raf = 0;
    let lastFrame = 0;
    const tick = (frameTime) => {
      raf = requestAnimationFrame(tick);
      if (document.hidden) return;
      if (frameTime - lastFrame < FRAME_MS) return;
      lastFrame = frameTime;
      const now = Date.now();
      // Both setState calls are auto-batched into one render, so the two
      // sources and the diff line update together from the same frame.
      if (trainsRef.current) {
        setInterpolatedTrains(trainsRef.current.map(train => interpolateTrain(train, now)));
      }
      if (darwinTrainsRef.current) {
        setDarwinInterpolatedTrains(darwinTrainsRef.current.map(train => interpolateDarwinTrain(train, now)));
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Fetch connected ADB device name
  useEffect(() => {
    fetchJson(`${API_BASE}/api/adb-device`)
      .then(data => setAdbDevice(data.device || null))
      .catch(() => setAdbDevice(null));
  }, []);

  // Fetch connected iOS simulator UDID
  useEffect(() => {
    fetchJson(`${API_BASE}/api/simctl-device`)
      .then(data => setSimctlDevice(data.device || null))
      .catch(() => setSimctlDevice(null));
  }, []);

  // Fetch Darwin train locations every 10s
  useEffect(() => {
    const fetchDarwin = async () => {
      const result = await fetchDarwinLocations();
      if (!result || !Array.isArray(result.trains)) return;
      const now = Date.now();
      setDarwinTrains(prev => {
        const incomingMap = new Map(result.trains.map(t => [String(t.rid), t]));
        const updated = [];
        const seenRids = new Set();

        for (const t of result.trains) {
          updated.push({ ...t, _lastSeenMs: now });
          seenRids.add(String(t.rid));
        }

        // Retain existing active trains if temporarily missing from an intermediate poll
        for (const oldTrain of (prev || [])) {
          const ridKey = String(oldTrain.rid);
          if (!seenRids.has(ridKey)) {
            if (now - (oldTrain._lastSeenMs || now) < 45000) {
              updated.push(oldTrain);
            }
          }
        }

        darwinTrainsRef.current = updated;
        return updated;
      });
      setDarwinStats({ connected: result.connected, ...result.stats });
    };
    fetchDarwin();
    const interval = setInterval(fetchDarwin, 10000);
    return () => clearInterval(interval);
  }, []);

  // Live accuracy stats: poll /api/accuracy-test every 30s while the
  // dashboard is visible AND both sources are on (otherwise the panel
  // hides itself - the comparison only makes sense with both feeds).
  useEffect(() => {
    if (!showAccuracyPanel || dataSource !== 'both') {
      setAccuracyStats(null);
      return;
    }
    let cancelled = false;
    let inFlight = false;
    let controller = null;
    const fetchAcc = async () => {
      if (inFlight) return;
      inFlight = true;
      controller = new AbortController();
      try {
        const res = await fetch(`${API_BASE}/api/accuracy-test`, { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setAccuracyStats(data);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Accuracy stats are diagnostic; live map updates should continue.
        }
      } finally {
        inFlight = false;
      }
    };
    fetchAcc();
    const interval = setInterval(fetchAcc, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      controller?.abort();
    };
  }, [showAccuracyPanel, dataSource]);

  // Live Darwin updates via socket
  useEffect(() => {
    const handleDarwinUpdate = (train) => {
      if (!train?.rid) return;
      setDarwinTrains(prev => {
        const existing = prev || [];
        const idx = existing.findIndex(t => t.rid === train.rid);
        let next;
        if (idx >= 0) {
          next = [...existing];
          next[idx] = { ...existing[idx], ...train };
        } else {
          next = [...existing, train];
        }
        darwinTrainsRef.current = next;
        return next;
      });
    };
    socket.on('darwinTrainUpdate', handleDarwinUpdate);
    return () => {
      socket.off('darwinTrainUpdate', handleDarwinUpdate);
    };
  }, []);

  useEffect(() => {
    let interval;
    let abortController;
    let active = true;

    if (selectedTrainRid) {
      const fetchDetail = async ({ showLoading = false } = {}) => {
        abortController?.abort();
        abortController = new AbortController();
        let didTimeout = false;
        const timeoutId = setTimeout(() => {
          didTimeout = true;
          abortController.abort();
        }, TRAIN_DETAIL_TIMEOUT_MS);

        if (showLoading) {
          setTrainDetailLoading(true);
        }

        try {
          const detail = await fetchTrainDetail(selectedTrainRid, abortController.signal);
          if (!active) return;

          if (!hasUsableTrainDetail(detail)) {
            setTrainDetail(null);
            setTrainDetailError("Journey details are not available for this train yet.");
            return;
          }

          setTrainDetail(detail);
          setTrainDetailError(null);
        } catch (err) {
          if (!active) return;
          if (err.name === "AbortError" && !didTimeout) return;
          console.error("Failed to fetch train detail:", err);
          setTrainDetail(null);
          setTrainDetailError(
            didTimeout
              ? "Journey details took too long to load from Signalbox."
              : "Could not load journey details from Signalbox."
          );
        } finally {
          clearTimeout(timeoutId);
          if (active && showLoading) {
            setTrainDetailLoading(false);
          }
        }
      };

      setTrainDetail(null);
      setTrainDetailError(null);
      fetchDetail({ showLoading: true });
      interval = setInterval(() => fetchDetail(), TRAIN_DETAIL_REFRESH_MS);
    } else {
      setTrainDetail(null);
      setTrainDetailError(null);
      setTrainDetailLoading(false);
    }

    return () => {
      active = false;
      abortController?.abort();
      clearInterval(interval);
    };
  }, [selectedTrainRid]);

  const findTrainByRid = useCallback((rid) => {
    if (!rid) return null;
    const ridStr = String(rid);
    const signalboxTrain = (interpolatedTrains || trains || []).find((train) => String(train.rid) === ridStr);
    if (signalboxTrain) return signalboxTrain;
    return displayedDarwinTrains.find((train) => String(train.rid) === ridStr) || null;
  }, [displayedDarwinTrains, interpolatedTrains, trains]);

  useEffect(() => {
    const handleTrackingLocationUpdate = (coords) => {
      setLastUpdatedCoords(coords);
      setTrackingLoading(false);
      const rid = coords.trainId || trackedTrainId;
      if (rid) {
        const currentTrain = findTrainByRid(rid);
        if (currentTrain) {
          setTrackedTrainInfo(prevInfo => ({
            ...prevInfo,
            id: currentTrain.rid,
            toc: TOC_NAMES[currentTrain.toc_code] || currentTrain.toc_code,
            lastLat: formatCoordinate(coords.latitude),
            lastLon: formatCoordinate(coords.longitude),
          }));
        }
      }
    };
    socket.on('trackingLocationUpdate', handleTrackingLocationUpdate);
    return () => {
      socket.off('trackingLocationUpdate', handleTrackingLocationUpdate);
    };
  }, [findTrainByRid, trackedTrainId]);

  const handleTrackTrain = useCallback((trainId) => {
    setTrackedTrainId(trainId);
    setTrackingLoading(true);
    socket.emit("startTracking", trainId);
    const currentTrain = findTrainByRid(trainId);
    const loc = getDisplayLocation(currentTrain);
    if (currentTrain) {
      setTrackedTrainInfo({
        id: currentTrain.rid,
        toc: TOC_NAMES[currentTrain.toc_code] || currentTrain.toc_code,
        lastLat: formatCoordinate(loc?.lat),
        lastLon: formatCoordinate(loc?.lon),
      });
    }
  }, [findTrainByRid]);

  const handleStopTracking = () => {
    socket.emit("stopTracking");
    setTrackedTrainId(null);
    setLastUpdatedCoords(null);
    setTrackedTrainInfo(null);
    setTrackingLoading(false);
  };

  const handleSelectTrain = useCallback((rid) => setSelectedTrainRid(rid), []);

  // Find matched trains from both sources for comparison
  const getMatchedTrains = useCallback((rid) => {
    if (!rid) return { signalbox: null, darwin: null };
    const ridStr = String(rid);
    const signalbox = (interpolatedTrains || trains)?.find(t => String(t.rid) === ridStr) || null;
    const darwin = displayedDarwinTrains.find(t => String(t.rid) === ridStr) || null;
    return { signalbox, darwin };
  }, [displayedDarwinTrains, interpolatedTrains, trains]);

  const matchedTrains = useMemo(() => getMatchedTrains(selectedTrainRid), [getMatchedTrains, selectedTrainRid]);
  const selectedTrainSummary = matchedTrains.signalbox || matchedTrains.darwin;

  // Handle map click on train layers
  const onMapClick = useCallback((event) => {
    const features = event.features;
    if (features && features.length > 0) {
      const feature = features[0];
      if (feature.properties?.rid) {
        handleSelectTrain(feature.properties.rid);
      }
    }
  }, [handleSelectTrain]);

  const onMouseEnter = useCallback(() => setCursor('pointer'), []);
  const onMouseLeave = useCallback(() => setCursor('grab'), []);

  // Hover preview: when the pointer is over a train layer, surface a
  // tooltip with headcode / TOC / bearing / delay. Hide on map-move-out.
  const onMapMouseMove = useCallback((event) => {
    const f = event.features?.[0];
    if (!f?.properties?.rid) {
      setHoveredTrain(null);
      return;
    }
    setHoveredTrain({
      screenX: event.point?.x ?? 0,
      screenY: event.point?.y ?? 0,
      train: f.properties
    });
  }, []);
  const onMapMouseLeave = useCallback(() => setHoveredTrain(null), []);

  const interactiveLayerIds = useMemo(() => {
    const ids = [];
    if (dataSource === 'signalbox' || dataSource === 'both') ids.push('signalbox-trains');
    if (dataSource === 'darwin' || dataSource === 'both') ids.push('darwin-trains');
    return ids;
  }, [dataSource]);

  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100vw',
        background: 'radial-gradient(ellipse at top, #1e293b 0%, #0f172a 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '20px',
        boxSizing: 'border-box'
      }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          background: 'rgba(30, 41, 59, 0.85)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          borderRadius: '24px',
          padding: '36px 32px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 40px rgba(99, 102, 241, 0.25)',
          color: '#f8fafc'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '28px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 25px -5px rgba(99, 102, 241, 0.5)',
              marginBottom: '16px'
            }}>
              <Train style={{ width: '32px', height: '32px', color: '#ffffff' }} />
            </div>
            <h1 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 8px 0', letterSpacing: '-0.5px', color: '#ffffff' }}>
              liamgregory.com
            </h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', margin: 0, lineHeight: 1.4 }}>
              Sign in
            </p>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#cbd5e1', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Enter password..."
                  autoFocus
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '0 44px 0 16px',
                    borderRadius: '12px',
                    background: 'rgba(15, 23, 42, 0.8)',
                    border: authError ? '1px solid #ef4444' : '1px solid #475569',
                    color: '#ffffff',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {authError && (
                <div style={{ marginTop: '10px', fontSize: '13px', color: '#f87171', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>⚠️</span> {authError}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={authLoading || !passwordInput}
              style={{
                width: '100%',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                color: '#ffffff',
                fontSize: '15px',
                fontWeight: 600,
                border: 'none',
                cursor: authLoading || !passwordInput ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: authLoading || !passwordInput ? 0.6 : 1
              }}
            >
              {authLoading ? <Loader2 className="animate-spin h-5 w-5" /> : <Lock size={18} />}
              {authLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {selectedTrainRid && (
        <div className="train-details-panel">
            {/* Header with close button */}
            <div className="train-details-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '12px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Train className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="train-details-title">Train Details</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Live tracking info</div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedTrainRid(null)}
                aria-label="Close train details"
                style={{ borderRadius: '8px' }}
              >
                <X className="w-5 h-5 text-slate-500" aria-hidden="true" />
              </Button>
            </div>

            {trainDetailLoading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Loader2 className="animate-spin h-8 w-8 text-slate-400" />
              </div>
            ) : trainDetail ? (
              <div className="train-details-content">
                {/* Journey Card */}
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderRadius: '16px',
                  padding: '20px',
                  marginBottom: '16px'
                }}>
                  {/* Operator & Status Row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                    <Badge variant="secondary" style={{
                      background: '#e0e7ff', color: '#4338ca', fontWeight: 600, fontSize: '11px'
                    }}>
                      {trainDetail.train_operator}
                    </Badge>
                    <Badge
                      style={{
                        background: trainDetail.delay > 5 ? '#fef2f2' : trainDetail.delay > 0 ? '#fffbeb' : '#f0fdf4',
                        color: trainDetail.delay > 5 ? '#dc2626' : trainDetail.delay > 0 ? '#d97706' : '#16a34a',
                        fontWeight: 600, fontSize: '11px'
                      }}
                    >
                      {trainDetail.delay > 0 ? `${trainDetail.delay} min late` : "On time"}
                    </Badge>
                  </div>

                  {/* Origin to Destination */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>{trainDetail.origin_name}</div>
                      <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#475569', marginTop: '2px' }}>
                        <Clock className="w-3 h-3 inline mr-1" style={{ verticalAlign: 'middle' }} />
                        {formatTime(trainDetail.origin_departure)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 8px' }}>
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </div>
                    <div style={{ flex: 1, textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>To</div>
                      <div style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>{trainDetail.destination_name}</div>
                      <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#475569', marginTop: '2px' }}>
                        <Clock className="w-3 h-3 inline mr-1" style={{ verticalAlign: 'middle' }} />
                        {formatTime(trainDetail.destination_arrival)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live Position Card */}
                <div style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Navigation className="w-3 h-3 inline mr-1" style={{ verticalAlign: 'middle' }} />
                    Live Position
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{
                      padding: '12px',
                      borderRadius: '8px',
                      background: matchedTrains.signalbox ? '#f8fafc' : '#fafafa',
                      opacity: matchedTrains.signalbox ? 1 : 0.5
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1e293b' }}></span>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Signalbox</span>
                      </div>
                      {matchedTrains.signalbox ? (
                        <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#64748b' }}>
                          {(matchedTrains.signalbox.interpolated_location?.lat || matchedTrains.signalbox.location?.lat)?.toFixed(4)},
                          {(matchedTrains.signalbox.interpolated_location?.lon || matchedTrains.signalbox.location?.lon)?.toFixed(4)}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>—</div>
                      )}
                    </div>
                    <div style={{
                      padding: '12px',
                      borderRadius: '8px',
                      background: matchedTrains.darwin ? '#fff7ed' : '#fafafa',
                      opacity: matchedTrains.darwin ? 1 : 0.5
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f97316' }}></span>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Darwin</span>
                      </div>
                      {matchedTrains.darwin ? (
                        <>
                          <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#64748b' }}>
                            {matchedTrains.darwin.location?.lat?.toFixed(4)},
                            {matchedTrains.darwin.location?.lon?.toFixed(4)}
                          </div>
                          {matchedTrains.darwin.estimatedSpeedMph && (
                            <div style={{ fontSize: '10px', color: '#f97316', marginTop: '4px' }}>
                              <Gauge className="w-3 h-3 inline mr-1" style={{ verticalAlign: 'middle' }} />
                              ~{Math.round(matchedTrains.darwin.estimatedSpeedMph)} mph
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>—</div>
                      )}
                    </div>
                  </div>
                  {matchedTrains.signalbox && matchedTrains.darwin && (
                    <div style={{
                      marginTop: '12px',
                      padding: '8px 12px',
                      background: '#f0f9ff',
                      borderRadius: '6px',
                      fontSize: '11px',
                      color: '#0369a1'
                    }}>
                      <span style={{ fontWeight: 600 }}>Position offset: </span>
                      <span style={{ fontFamily: 'monospace' }}>
                        {(() => {
                          const sbLoc = matchedTrains.signalbox.interpolated_location || matchedTrains.signalbox.location;
                          const dwLoc = matchedTrains.darwin.location;
                          if (!sbLoc || !dwLoc) return 'N/A';
                          const dLat = (sbLoc.lat - dwLoc.lat) * 111000;
                          const dLon = (sbLoc.lon - dwLoc.lon) * 111000 * Math.cos(sbLoc.lat * Math.PI / 180);
                          const dist = Math.sqrt(dLat * dLat + dLon * dLon);
                          return `~${Math.round(dist)}m`;
                        })()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Calling Pattern */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                  }}>
                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <MapPin className="w-3 h-3 inline mr-1" style={{ verticalAlign: 'middle' }} />
                      Calling Pattern
                    </div>
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>{(trainDetail.stops || []).length} stops</span>
                  </div>

                  <div style={{ maxHeight: '280px', overflowY: 'auto', paddingRight: '4px' }}>
                    {(trainDetail.stops || []).map((stop, idx) => {
                      const status = getStopStatus(stop, Date.now());
                      const isFirst = idx === 0;
                      const isLast = idx === (trainDetail.stops || []).length - 1;
                      const isPast = status === 'Departed' || status === 'Arrived';

                      return (
                        <div key={stop.crs || stop.name || idx} style={{
                          display: 'flex',
                          gap: '12px',
                          position: 'relative'
                        }}>
                          {/* Timeline */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            width: '20px',
                            flexShrink: 0
                          }}>
                            {!isFirst && (
                              <div style={{
                                width: '2px',
                                height: '12px',
                                background: isPast ? '#22c55e' : '#e2e8f0'
                              }}></div>
                            )}
                            <div style={{
                              width: isFirst || isLast ? '12px' : '10px',
                              height: isFirst || isLast ? '12px' : '10px',
                              borderRadius: '50%',
                              background: isPast ? '#22c55e' : status === 'Due' ? '#f59e0b' : '#e2e8f0',
                              border: isFirst || isLast ? '2px solid' : 'none',
                              borderColor: isPast ? '#16a34a' : '#cbd5e1',
                              flexShrink: 0
                            }}></div>
                            {!isLast && (
                              <div style={{
                                width: '2px',
                                flex: 1,
                                minHeight: '24px',
                                background: isPast ? '#22c55e' : '#e2e8f0'
                              }}></div>
                            )}
                          </div>

                          {/* Stop Details */}
                          <div style={{
                            flex: 1,
                            paddingBottom: isLast ? 0 : '12px',
                            minWidth: 0
                          }}>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: '8px'
                            }}>
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{
                                  fontSize: '13px',
                                  fontWeight: 600,
                                  color: isPast ? '#64748b' : '#1e293b',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {stop.name}
                                </div>
                                <div style={{
                                  fontSize: '11px',
                                  color: '#94a3b8',
                                  marginTop: '2px'
                                }}>
                                  {stop.platform && <span>Platform {stop.platform}</span>}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                <div style={{
                                  fontFamily: 'monospace',
                                  fontSize: '12px',
                                  color: isPast ? '#64748b' : '#1e293b',
                                  fontWeight: 500
                                }}>
                                  {formatTime(stop.arrival) !== '—' ? formatTime(stop.arrival) : formatTime(stop.departure)}
                                </div>
                                <div style={{
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  marginTop: '2px',
                                  color: status === 'Cancelled' ? '#dc2626' :
                                         status === 'Departed' ? '#22c55e' :
                                         status === 'Arrived' ? '#16a34a' :
                                         status === 'Due' ? '#f59e0b' : '#64748b'
                                }}>
                                  {status}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '12px',
                  paddingTop: '8px',
                  borderTop: '1px solid #f1f5f9'
                }}>
                  <Button
                    variant="gradient"
                    onClick={() => handleTrackTrain(trainDetail.rid)}
                    style={{
                      height: '44px',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                    }}
                  >
                    <Smartphone className="w-4 h-4 mr-2" />
                    Track on Device
                  </Button>
                  <div style={{
                    height: '44px',
                    padding: '0 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f8fafc',
                    borderRadius: '10px',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    color: '#64748b',
                    border: '1px solid #e2e8f0'
                  }}>
                    {trainDetail.rid}
                  </div>
                </div>
              </div>
            ) : (
              <div className="train-details-content">
                <div style={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderRadius: '16px',
                  padding: '20px',
                  marginBottom: '16px'
                }}>
                  <Badge variant="secondary" style={{
                    background: '#e0e7ff', color: '#4338ca', fontWeight: 600, fontSize: '11px', marginBottom: '12px'
                  }}>
                    {TOC_NAMES[selectedTrainSummary?.toc_code] || selectedTrainSummary?.toc_code || "Selected train"}
                  </Badge>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
                    Journey details unavailable
                  </div>
                  <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.5, color: '#64748b' }}>
                    {trainDetailError || "Live map position is available, but the calling pattern has not been returned yet."}
                  </p>
                  <div style={{
                    marginTop: '14px',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                  }}>
                    <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>RID</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#1e293b' }}>{selectedTrainRid}</span>
                  </div>
                  <Button
                    variant="gradient"
                    onClick={() => handleTrackTrain(selectedTrainRid)}
                    style={{
                      marginTop: '12px',
                      width: '100%',
                      height: '44px',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                    }}
                  >
                    <Smartphone className="w-4 h-4 mr-2" />
                    Track on Device
                  </Button>
                </div>

                <div style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '16px'
                }}>
                  <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <Navigation className="w-3 h-3 inline mr-1" style={{ verticalAlign: 'middle' }} />
                    Live Position
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div style={{
                      padding: '12px',
                      borderRadius: '8px',
                      background: matchedTrains.signalbox ? '#f8fafc' : '#fafafa',
                      opacity: matchedTrains.signalbox ? 1 : 0.5
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#1e293b' }}></span>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Signalbox</span>
                      </div>
                      {matchedTrains.signalbox ? (
                        <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#64748b' }}>
                          {formatCoordinate((matchedTrains.signalbox.interpolated_location || matchedTrains.signalbox.location)?.lat)},
                          {formatCoordinate((matchedTrains.signalbox.interpolated_location || matchedTrains.signalbox.location)?.lon)}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>—</div>
                      )}
                    </div>
                    <div style={{
                      padding: '12px',
                      borderRadius: '8px',
                      background: matchedTrains.darwin ? '#fff7ed' : '#fafafa',
                      opacity: matchedTrains.darwin ? 1 : 0.5
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f97316' }}></span>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569' }}>Darwin</span>
                      </div>
                      {matchedTrains.darwin ? (
                        <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#64748b' }}>
                          {formatCoordinate((matchedTrains.darwin.interpolated_location || matchedTrains.darwin.location)?.lat)},
                          {formatCoordinate((matchedTrains.darwin.interpolated_location || matchedTrains.darwin.location)?.lon)}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>—</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
      )}
      <div className="main-content-wrapper">
        {/* Header */}
        <header className="header">
          <div className="header-brand">
            <Avatar className="brand-avatar bg-purple-600">
              <AvatarFallback className="bg-purple-600 text-white">
                <Train className="w-4 h-4" aria-hidden="true" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <h1 className="header-title">Train Tracker</h1>
              <p className="header-subtitle">Live UK Train Map</p>
            </div>
          </div>

          <div className="header-controls">
            <div className="status-group">
              <div className="status-item">
                <Smartphone className="w-4 h-4 text-slate-600" aria-hidden="true" />
                <span className="control-label">Android</span>
                {adbDevice ? (
                  <Badge variant="success" className="font-mono text-xs px-2 py-0.5 truncate" title={adbDevice}>{adbDevice}</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs px-2 py-1">Not connected</Badge>
                )}
              </div>
              <Separator orientation="vertical" className="header-separator" />
              <div className="status-item">
                <Smartphone className="w-4 h-4 text-slate-600" aria-hidden="true" />
                <span className="control-label">iOS</span>
                {simctlDevice ? (
                  <Badge variant="success" className="font-mono text-xs px-2 py-0.5 truncate" title={simctlDevice}>{simctlDevice.slice(0, 8)}…</Badge>
                ) : (
                  <Badge variant="outline" className="text-xs px-2 py-1">Not connected</Badge>
                )}
              </div>
              <Separator orientation="vertical" className="header-separator" />
              <div className="status-item">
                <span className="control-label">Darwin</span>
                {darwinStats.connected ? (
                  <Badge variant="success" className="text-xs px-2 py-0.5">
                    {darwinStats.trainCount || 0} trains
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs px-2 py-0.5">Offline</Badge>
                )}
              </div>
            </div>

            <div className="source-toggle" aria-label="Visible train data sources">
              <span className="control-label">Show</span>
              <Button
                variant={dataSource === 'signalbox' || dataSource === 'both' ? "default" : "outline"}
                size="sm"
                className="compact-button"
                aria-pressed={dataSource === 'signalbox' || dataSource === 'both'}
                onClick={() => setDataSource(prev => prev === 'darwin' ? 'both' : prev === 'both' ? 'darwin' : 'both')}
              >
                Signalbox
              </Button>
              <Button
                variant={dataSource === 'darwin' || dataSource === 'both' ? "default" : "outline"}
                size="sm"
                className="compact-button darwin-button"
                aria-pressed={dataSource === 'darwin' || dataSource === 'both'}
                onClick={() => setDataSource(prev => prev === 'signalbox' ? 'both' : prev === 'both' ? 'signalbox' : 'both')}
              >
                Darwin
              </Button>
            </div>

            <div className="map-toggles">
              <Button
                variant={showNetworkLines ? "default" : "outline"}
                size="sm"
                className="toolbar-button"
                aria-pressed={showNetworkLines}
                onClick={() => setShowNetworkLines((v) => !v)}
              >
                <Route className="w-4 h-4" aria-hidden="true" />
                {showNetworkLines ? "Hide Network" : "Show Network"}
              </Button>
              <Button
                variant={showTiplocs ? "default" : "outline"}
                size="sm"
                className="toolbar-button"
                aria-pressed={showTiplocs}
                onClick={() => setShowTiplocs((v) => !v)}
              >
                <MapPin className="w-4 h-4" aria-hidden="true" />
                {showTiplocs ? "Hide TIPLOCs" : "Show TIPLOCs"}
              </Button>
              <Button
                variant={showDiffLines ? "default" : "outline"}
                size="sm"
                className="toolbar-button"
                aria-pressed={showDiffLines}
                disabled={dataSource !== 'both'}
                title={dataSource !== 'both' ? 'Requires both sources visible' : 'Toggle Signalbox vs Darwin diff lines'}
                onClick={() => setShowDiffLines((v) => !v)}
              >
                {showDiffLines ? "Hide Diffs" : "Show Diffs"}
              </Button>
              <Button
                variant={showAccuracyPanel ? "default" : "outline"}
                size="sm"
                className="toolbar-button"
                aria-pressed={showAccuracyPanel}
                disabled={dataSource !== 'both'}
                title={dataSource !== 'both' ? 'Requires both sources visible' : 'Toggle live accuracy panel'}
                onClick={() => setShowAccuracyPanel((v) => !v)}
              >
                {showAccuracyPanel ? "Hide Accuracy" : "Show Accuracy"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="toolbar-button"
                title="Lock Application"
                onClick={handleLogout}
              >
                <Lock className="w-4 h-4 mr-1" aria-hidden="true" />
                Lock
              </Button>
            </div>
          </div>
        </header>

        {/* Main Content - MapGL */}
        <main className="map-wrapper">
          {/* Render as soon as EITHER feed has data. Gating the whole map on
              Signalbox alone left it blank whenever that feed stalled, even
              with thousands of live Darwin trains ready to draw. */}
          {(trains || (darwinTrains && darwinTrains.length > 0)) ? (
            <MapGL
              initialViewState={{
                ...mapCenter,
                zoom: 6
              }}
              style={{ width: '100%', height: '100%' }}
              mapStyle={MAP_STYLE}
              cursor={cursor}
              onClick={onMapClick}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onMouseMove={onMapMouseMove}
              onMouseOut={onMapMouseLeave}
              onLoad={onMapLoad}
              interactiveLayerIds={interactiveLayerIds}
            >
              {/* Network Lines Layer */}
              {showNetworkLines && networkLines && (
                <Source id="network-lines" type="geojson" data={networkLines}>
                  <Layer {...NETWORK_LINE_LAYER} />
                </Source>
              )}

              {/* TIPLOC Points Layer */}
              {showTiplocs && tiplocGeoJson && (
                <Source id="tiplocs" type="geojson" data={tiplocGeoJson}>
                  <Layer {...TIPLOC_STATION_LAYER} />
                  <Layer {...TIPLOC_POINT_LAYER} />
                  <Layer {...TIPLOC_STATION_LABEL_LAYER} />
                  <Layer {...TIPLOC_POINT_LABEL_LAYER} />
                </Source>
              )}

              {/* Signalbox vs Darwin diff lines (only when both visible + toggled) */}
              {diffLinesGeoJson && (
                <Source id="diff-lines" type="geojson" data={diffLinesGeoJson}>
                  <Layer
                    id="diff-lines-glow"
                    type="line"
                    paint={{ 'line-color': '#ef4444', 'line-width': 4, 'line-opacity': 0.18, 'line-blur': 1 }}
                  />
                  <Layer
                    id="diff-lines-line"
                    type="line"
                    paint={{ 'line-color': '#ef4444', 'line-width': 1.5, 'line-opacity': 0.85, 'line-dasharray': [2, 2] }}
                  />
                </Source>
              )}

              {/* Selected train's interpolation track (route_geometry).
                  Rendered under the icons so the train symbol stays on top. */}
              {mapLoaded && selectedRouteGeoJson && (
                <Source id="selected-route" type="geojson" data={selectedRouteGeoJson}>
                  <Layer
                    id="selected-route-glow"
                    type="line"
                    layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                    paint={{ 'line-color': '#0ea5e9', 'line-width': 7, 'line-opacity': 0.25, 'line-blur': 2 }}
                  />
                  <Layer
                    id="selected-route-line"
                    type="line"
                    layout={{ 'line-cap': 'round', 'line-join': 'round' }}
                    paint={{ 'line-color': '#0ea5e9', 'line-width': 2.5, 'line-opacity': 0.9 }}
                  />
                </Source>
              )}

              {/* Signalbox Trains - single symbol layer (circle + arrow inside) */}
              {mapLoaded && (dataSource === 'signalbox' || dataSource === 'both') && signalboxGeoJson && (
                <Source id="signalbox-trains" type="geojson" data={signalboxGeoJson}>
                  <Layer {...SIGNALBOX_TRAIN_LAYER} />
                </Source>
              )}

              {/* Darwin Trains - single symbol layer (circle + arrow inside) */}
              {mapLoaded && (dataSource === 'darwin' || dataSource === 'both') && darwinGeoJson && (
                <Source id="darwin-trains" type="geojson" data={darwinGeoJson}>
                  <Layer {...DARWIN_TRAIN_LAYER} />
                </Source>
              )}
            </MapGL>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="animate-spin h-12 w-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-300 font-medium">
                  {trainLoadError || "Loading train data..."}
                </p>
              </div>
            </div>
          )}

          {/* Live accuracy dashboard - hidden unless both sources visible */}
          {showAccuracyPanel && dataSource === 'both' && accuracyStats && (
            <div
              style={{
                position: 'absolute', top: 12, right: 12, zIndex: 10,
                background: 'rgba(15, 23, 42, 0.88)', color: '#e2e8f0',
                padding: '10px 14px', borderRadius: 10, fontSize: 12,
                lineHeight: 1.5, minWidth: 220, fontFamily: 'system-ui, sans-serif',
                backdropFilter: 'blur(4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
                Live accuracy vs Signalbox
              </div>
              <div>Match rate: <b>{accuracyStats.summary?.matchRate}</b> ({accuracyStats.summary?.matchingTrains}/{accuracyStats.summary?.signalboxTrains})</div>
              <div style={{ marginTop: 4 }}>
                TD ({accuracyStats.bySource?.td?.count}): med <b>{accuracyStats.bySource?.td?.medianMeters}m</b> / p90 <b>{accuracyStats.bySource?.td?.p90Meters}m</b>
              </div>
              <div>
                Darwin ({accuracyStats.bySource?.darwin?.count}): med <b>{accuracyStats.bySource?.darwin?.medianMeters}m</b> / p90 <b>{accuracyStats.bySource?.darwin?.p90Meters}m</b>
              </div>
              <div style={{ marginTop: 4, opacity: 0.8 }}>
                Overall: med <b>{accuracyStats.accuracy?.medianErrorMeters}m</b> / p90 <b>{accuracyStats.accuracy?.p90ErrorMeters}m</b>
              </div>
            </div>
          )}

          {/* Hover preview tooltip */}
          {hoveredTrain && (
            <div
              style={{
                position: 'absolute', left: hoveredTrain.screenX + 14, top: hoveredTrain.screenY + 14,
                zIndex: 11, pointerEvents: 'none',
                background: 'rgba(15, 23, 42, 0.92)', color: '#e2e8f0',
                padding: '6px 10px', borderRadius: 8, fontSize: 12,
                fontFamily: 'system-ui, sans-serif', whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
              }}
            >
              <div><b>{hoveredTrain.train.trainId || hoveredTrain.train.rid}</b> · {hoveredTrain.train.toc_code || '—'}</div>
              <div style={{ opacity: 0.85 }}>
                Source: {hoveredTrain.train.source} · Bearing {Math.round(hoveredTrain.train.bearing || 0)}°
                {Number.isFinite(hoveredTrain.train.delay ?? hoveredTrain.train.delayMinutes)
                  ? ` · ${hoveredTrain.train.delay ?? hoveredTrain.train.delayMinutes} min`
                  : ''}
                {Number.isFinite(Number(hoveredTrain.train.confidence))
                  ? ` · conf ${Number(hoveredTrain.train.confidence).toFixed(2)}`
                  : ''}
              </div>
            </div>
          )}

          {/* Tracked Train Info */}
          {trackedTrainInfo && (
            <Card
              className="tracker-info-box"
              style={{
                background: "white",
                borderRadius: "1rem",
                boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.18)",
                border: "1px solid #e5e7eb",
                minWidth: 320,
                maxWidth: 400,
              }}
            >
              <CardHeader className="tracker-info-header">
                <Avatar className="w-11 h-11 bg-purple-500">
                  <AvatarFallback className="bg-purple-500 text-white">
                    <Train className="w-6 h-6" />
                  </AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="tracker-info-title">Tracking Train</CardTitle>
                  <p className="tracker-info-subtitle">Live Location Updates</p>
                </div>
              </CardHeader>
              <CardContent className="tracker-info-content">
                <div>
                  <div className="tracker-info-item">
                    <span>Train ID</span>
                    <Badge variant="outline" className="font-mono text-xs px-2 py-0.5 ml-1">{trackedTrainInfo.id}</Badge>
                  </div>
                </div>
                <div>
                  <div className="tracker-info-item">
                    <span>Train Operator</span>
                    <Badge variant="secondary" className="ml-1 text-xs px-2 py-0.5">{trackedTrainInfo.toc}</Badge>
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-slate-500 font-semibold mb-1">Latest GPS Coordinates</p>
                  {trackingLoading ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="animate-spin h-4 w-4" />
                      <span className="text-xs">Waiting for GPS update...</span>
                    </div>
                  ) : lastUpdatedCoords ? (
                    <div className="tracker-info-coords">
                      <div>Lat: {lastUpdatedCoords.latitude.toFixed(6)}</div>
                      <div>Lon: {lastUpdatedCoords.longitude.toFixed(6)}</div>
                    </div>
                  ) : (
                    <div className="tracker-info-coords">
                      <div>Lat: {trackedTrainInfo.lastLat}</div>
                      <div>Lon: {trackedTrainInfo.lastLon}</div>
                    </div>
                  )}
                </div>
              </CardContent>
              <Separator />
              <div className="tracker-info-footer">
                <Button
                  variant="gradientDestructive"
                  className="w-full h-11 text-base font-semibold"
                  onClick={handleStopTracking}
                >
                  Stop Tracking
                </Button>
              </div>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;
